import { searchMembersQuery } from "@/api/member";
import { useDebouncedQuery } from "@/api/utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { CountryFlagImage } from "@/components/ui/CountryFlagImage";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { copyToClipboard } from "@/utils/clipboard";
import { seo } from "@/utils/seo";
import { titleCase } from "@common/utils/string";
import { Flex } from "@givenwell/components";
import { CopyIcon } from "@givenwell/core-solid-icons";
import { createFileRoute } from "@tanstack/react-router";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/admin/members/")({
  component: MembersPage,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
  }),
  head: () => ({ meta: seo({ title: "Members" }) }),
});

function MembersPage() {
  const { q: searchTerm } = Route.useSearch();
  const query = useDebouncedQuery(searchMembersQuery(searchTerm));
  const rows = query.data?.members || [];

  return (
    <PageWrapper title="Members">
      <DataTable>
        <DataTableHeader autoFocusSearch hideToggle />
        <DataTableBody
          data={rows}
          rounded="bottom"
          emptyStateMessage={
            !searchTerm || query.isPending
              ? "Type in the search box above to find members"
              : "No matching members found"
          }
          status={query.status}
          dataKey="member_id"
          columns={[
            {
              header: "Name",
              cell: member => (
                <StyledLink to="/admin/members/$memberId" params={{ memberId: member.member_id }}>
                  {member.given_name} {member.family_name}
                </StyledLink>
              ),
            },
            {
              header: "Email",
              cell: m => (
                <Flex css={{ alignItems: "center" }}>
                  {m.email}{" "}
                  <IconButton
                    title="Copy to clipboard"
                    size="sm"
                    aria-label="Copy to clipboard"
                    variant="plain"
                    css={{ padding: 0, m: 0 }}
                    onClick={() => copyToClipboard(m.email)}
                  >
                    <CopyIcon width="14" height="14" />
                  </IconButton>
                </Flex>
              ),
            },
            {
              header: "Type",
              cell: m => titleCase(m.type.replace(/_/g, " ")),
            },
            {
              header: "Country",
              cell: member => (
                <>
                  <CountryFlagImage countryCode={member.country || ""} css={{ size: 24, display: "inline-block" }} />{" "}
                  {member.country}
                </>
              ),
            },
            {
              header: "Supporter(s)",
              cell: m => (
                <>
                  {m.supporters?.map(s => (
                    <StyledLink
                      key={s.supporter_id}
                      to="/admin/supporters/$supporterId"
                      params={{ supporterId: s.supporter_id }}
                    >
                      {s.name}
                    </StyledLink>
                  ))}
                </>
              ),
            },
            {
              header: "ID",
              css: { opacity: 0.5, fontFamily: "monospace", fontScale: "xs" },
              cell: m => (
                <Flex css={{ alignItems: "center" }}>
                  {m.member_id}{" "}
                  <IconButton
                    title="Copy to clipboard"
                    size="sm"
                    aria-label="Copy to clipboard"
                    variant="plain"
                    css={{ padding: 0, m: 0 }}
                    onClick={() => copyToClipboard(m.member_id)}
                  >
                    <CopyIcon width="14" height="14" />
                  </IconButton>
                </Flex>
              ),
            },
          ]}
        />
      </DataTable>
    </PageWrapper>
  );
}
